import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { sliders } from "../../utils/utils";

const AUTOPLAY_MS = 4000;

/**
 * Bannières de l'accueil : défilement natif (scroll-snap), sans dépendance.
 *
 * react-slick clonait les slides pour boucler et la bannière apparaissait en
 * double à l'écran ; ici il n'y a aucun clone, le retour de la dernière à la
 * première se fait par un simple défilement. Le geste tactile reste celui du
 * navigateur, donc fluide et sans rien à réimplémenter.
 */
const HomeSlider = () => {
  const trackRef = useRef(null);
  const indexRef = useRef(0);
  const pausedRef = useRef(false);
  const [index, setIndex] = useState(0);

  const goTo = useCallback((i) => {
    const track = trackRef.current;
    if (track) track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
  }, []);

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const current = Math.round(track.scrollLeft / track.clientWidth);
    indexRef.current = current;
    setIndex(current);
  };

  // Défilement automatique. Suspendu pendant le toucher / le survol, quand
  // l'onglet est caché, et jamais lancé si l'utilisateur préfère moins de
  // mouvement.
  useEffect(() => {
    if (sliders.length < 2) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;

    const timer = window.setInterval(() => {
      if (pausedRef.current || document.hidden) return;
      goTo((indexRef.current + 1) % sliders.length);
    }, AUTOPLAY_MS);

    return () => window.clearInterval(timer);
  }, [goTo]);

  const pause = () => {
    pausedRef.current = true;
  };
  const resume = () => {
    pausedRef.current = false;
  };

  return (
    <div
      className="home-slider relative w-full overflow-hidden"
      onTouchStart={pause}
      onTouchEnd={resume}
      onTouchCancel={resume}
      onMouseEnter={pause}
      onMouseLeave={resume}
    >
      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="flex w-full overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {sliders.map((slide, i) => {
          const image = (
            <img
              src={slide.src}
              alt={slide.alt}
              className="block w-full h-auto aspect-[940/459] object-cover object-center"
              // La première est visible au chargement ; les suivantes attendent.
              loading={i === 0 ? "eager" : "lazy"}
              draggable={false}
            />
          );

          return (
            <div key={slide.src} className="w-full shrink-0 snap-center">
              {slide.to ? <Link to={slide.to}>{image}</Link> : image}
            </div>
          );
        })}
      </div>

      {sliders.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
          {sliders.map((slide, i) => (
            <button
              key={slide.src}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Bannière ${i + 1} sur ${sliders.length}`}
              aria-current={i === index}
              className="p-1"
            >
              <span
                className={`block h-1.5 rounded-full transition-all ${
                  i === index ? "w-4 bg-white" : "w-1.5 bg-white/60"
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default HomeSlider;
